import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  downloadFile,
  request,
  type TicketPriority,
  type WorkComment,
  type WorkFile,
  type WorkStatus,
  type WorkTicket,
  type WorkTicketDetailResponse,
  type WorkTicketListResponse,
} from './client';

/**
 * Tickets — requests filed outside a meeting. docs/api-contract-tickets.md.
 *
 *   GET    /work/tickets?view=&q=&page=          tickets:read
 *   POST   /work/tickets                         tickets:read     in your own name; an owner other than yourself, a priority other than the default, or a dueOn need tickets:update (403, fieldErrors per field) · audit work_ticket.created
 *   GET    /work/tickets/{id}                    tickets:read
 *   PATCH  /work/tickets/{id}                    tickets:update, or the requester for title/details/status, or the owner for status
 *   POST   /work/tickets/{id}/comments           tickets:read     audit work_ticket.commented
 *   POST   /work/tickets/{id}/files              tickets:read     raw bytes, ?name=&commentId= · audit work_ticket.file_added
 *   GET    /work/tickets/{id}/files/{fileId}     tickets:read     the bytes · audit work_ticket.file_downloaded
 *   DELETE /work/tickets/{id}/files/{fileId}     whoever attached it, or tickets:update · audit work_ticket.file_removed
 *
 * Meeting action items are the other half of the screen; they come from
 * `/meetings/actions` (src/api/meetings.ts) and are settled through their
 * meeting.
 */

export const PRIORITY_LABEL: Record<TicketPriority, string> = {
  critical: 'Critical',
  normal: 'Normal',
  low: 'Low',
};

/** The same guide the API applies when no due date is given. */
export const PRIORITY_GUIDE: Record<TicketPriority, { days: number; line: string }> = {
  critical: { days: 0, line: 'Same business day — something is blocked or unsafe' },
  normal: { days: 2, line: 'Within 2 business days — the usual request' },
  low: { days: 5, line: 'Within 5 business days — whenever there is a moment' },
};

export const WORK_STATUS_LABEL: Record<WorkStatus, string> = {
  open: 'Open',
  done: 'Done',
  dropped: 'Dropped',
};

export type TicketListView = 'mine' | 'requested' | 'open' | 'closed';

export interface TicketFilters {
  view: TicketListView;
  q?: string;
  page?: number;
  pageSize?: number;
}

export const useWorkTickets = (filters: TicketFilters, enabled = true) =>
  useQuery({
    queryKey: ['work-tickets', filters],
    queryFn: () => request<WorkTicketListResponse>('/work/tickets', { query: { ...filters } }),
    placeholderData: (prev) => prev,
    enabled,
  });

export const useWorkTicket = (id: string | null) =>
  useQuery({
    queryKey: ['work-ticket', id],
    queryFn: () => request<WorkTicketDetailResponse>(`/work/tickets/${id}`),
    enabled: id !== null,
  });

/** Everything that changes a ticket invalidates the list and the record. */
function useTicketWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work-tickets'] });
      void qc.invalidateQueries({ queryKey: ['work-ticket'] });
    },
  });
}

export interface CreateTicketBody {
  title: string;
  details?: string | null;
  priority?: TicketPriority;
  ownerId?: string | null;
  studySlug?: string | null;
  dueOn?: string | null;
  /** Where it came from: a REDCap weekly flag, by its key. One open ticket per flag; a second is a 409 naming the first. */
  source?: { type: 'redcap_flag'; key: string } | null;
}

export const useCreateTicket = () =>
  useTicketWrite((body: CreateTicketBody) => request<{ ticket: WorkTicket }>('/work/tickets', { method: 'POST', body }));

export interface TicketPatch {
  title?: string;
  details?: string | null;
  priority?: TicketPriority;
  ownerId?: string | null;
  dueOn?: string | null;
  studySlug?: string | null;
  status?: WorkStatus;
}

export const useUpdateTicket = () =>
  useTicketWrite(({ id, ...patch }: TicketPatch & { id: string }) =>
    request<{ ticket: WorkTicket }>(`/work/tickets/${id}`, { method: 'PATCH', body: patch }),
  );

export const useAddTicketComment = () =>
  useTicketWrite(({ id, body }: { id: string; body: string }) =>
    request<{ comment: WorkComment }>(`/work/tickets/${id}/comments`, { method: 'POST', body: { body } }),
  );

/* ───────────────────────────────────────────────────────── files (052) */

/** The same limits the API applies, so most refusals never leave the browser. */
export const FILE_MAX_BYTES = 10 * 1024 * 1024;
export const FILES_PER_TICKET = 20;
export const FILE_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'csv', 'txt', 'md', 'docx', 'xlsx', 'pptx', 'eml', 'msg'] as const;
/** For the file input's `accept`. */
export const FILE_ACCEPT = FILE_EXTENSIONS.map((e) => `.${e}`).join(',');
export const FILE_KINDS = 'PDF, images, Office files, CSV, text and email messages';

/** Why a file cannot be attached, in plain words — or null when it can. `have` is how many the ticket already holds. */
export function fileProblem(file: File, have: number): string | null {
  const ext = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase();
  if (!ext || !(FILE_EXTENSIONS as readonly string[]).includes(ext)) {
    return `${file.name} is not a kind of file that can be attached. ${FILE_KINDS} can.`;
  }
  if (file.size === 0) return `${file.name} is empty.`;
  if (file.size > FILE_MAX_BYTES) return `${file.name} is over 10 MB.`;
  if (have >= FILES_PER_TICKET) return `A ticket holds up to ${FILES_PER_TICKET} files. Remove one first.`;
  return null;
}

/** The bytes go up as the body; the name and the update it belongs to in the query. The API reads the kind from the name. */
export const useAddTicketFile = () =>
  useTicketWrite(({ id, file, commentId }: { id: string; file: File; commentId?: string | null }) =>
    request<{ file: WorkFile }>(`/work/tickets/${id}/files`, {
      method: 'POST',
      query: { name: file.name, commentId: commentId ?? undefined },
      body: file,
      rawBody: true,
      contentType: 'application/octet-stream',
    }),
  );

export const useRemoveTicketFile = () =>
  useTicketWrite(({ id, fileId }: { id: string; fileId: string }) =>
    request<{ ok: boolean }>(`/work/tickets/${id}/files/${fileId}`, { method: 'DELETE' }),
  );

/** Through the authenticated client, saved under the name it was attached with. */
export const downloadTicketFile = (ticketId: string, file: Pick<WorkFile, 'id' | 'name'>): Promise<void> =>
  downloadFile(`/work/tickets/${ticketId}/files/${file.id}`, file.name);
